import {
  catchError,
  map,
  repeat,
  switchMap,
  tap,
} from 'rxjs/operators';
import { filter, of } from 'rxjs';
import { Apollo } from 'apollo-angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { WO_CONTEXT_HEADER } from '@iss/libs/user-context';
import { MyAuthDetailsGQL } from '@iss/libs/graphql';
import { AuthContext, ISS_AUTH_SERVICE } from '@iss/libs/auth';
import { Router } from '@angular/router';
import { inject, Injectable } from '@angular/core';

import { authActions } from './auth.actions';

import { httpCode, mapApolloResultToActions } from '@iss/wo-common/utils';

@Injectable()
export class AuthEffects {
  private readonly myAuthDetailsGQL = inject(MyAuthDetailsGQL);
  private readonly router = inject(Router);
  private readonly actions$ = inject(Actions);
  private readonly apollo = inject(Apollo);
  private readonly auth = inject(ISS_AUTH_SERVICE);

  public readonly fetchDetails$ = createEffect(() => this.actions$.pipe(
    ofType(authActions.authenticated),
    switchMap(() => this.myAuthDetailsGQL.watch(undefined, {
      fetchPolicy: 'no-cache',
      context: {
        headers: {
          [WO_CONTEXT_HEADER]: '',
        },
      },
    }).valueChanges),
    map(
      mapApolloResultToActions({
        loading: authActions.detailsLoading(),
        data: ({ myAuthDetails }) => authActions.detailsLoaded({ details: myAuthDetails }),
        error: () => authActions.detailsLoadingFailure({}),
      }),
    ),
    catchError(err => of(authActions.detailsLoadingFailure({ httpCode: httpCode(err) }))),
    repeat(),
  ));

  public showGlobalErrorError$ = createEffect(() => this.actions$.pipe(
    ofType(authActions.detailsLoadingFailure),
    filter(({ httpCode }) => httpCode !== 451 && httpCode !== 401),
    tap(() => void this.router.navigate([ '/', 'global-error' ], { skipLocationChange: true })),
  ), { dispatch: false });

  public showAuthErrorError$ = createEffect(() => this.actions$.pipe(
    ofType(authActions.detailsLoadingFailure),
    filter(({ httpCode }) => httpCode === 401),
    tap(() => void this.router.navigate([ '/', 'not-authorized' ], { skipLocationChange: true })),
  ), { dispatch: false });

  public login$ = createEffect(() => this.actions$.pipe(
    ofType(authActions.authenticated),
    tap(({ account }) => AuthContext.setEmail(account.email)),
  ), { dispatch: false });

  public logout$ = createEffect(() => this.actions$.pipe(
    ofType(authActions.logout),
    switchMap(({ withRequest }) => withRequest ? this.auth.logout() : of(undefined)),
    tap(() => {
      localStorage.clear();
      void this.apollo.client.clearStore();
    }),
  ), { dispatch: false });
}
